import { supabase } from './supabaseClient';

export type MusicServiceType = 'spotify' | 'apple';

export interface AuthToken {
  access_token: string;
  refresh_token?: string;
  expires_at?: string;
  user_identifier?: string;
  scopes?: string[];
}

/**
 * Saves a music service authentication token to Supabase for the current user
 */
export const saveAuthToken = async (
  providerType: MusicServiceType, 
  tokenData: AuthToken
): Promise<boolean> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('Cannot save token: No authenticated user');
      return false;
    }
    
    // Process expires_at if it's a timestamp in milliseconds
    let processedExpiresAt = tokenData.expires_at;
    if (processedExpiresAt && !isNaN(Number(processedExpiresAt))) {
      // Store as a string but not as a date to avoid PostgreSQL date/time range issues
      processedExpiresAt = String(processedExpiresAt);
    }
    
    // Check if an entry already exists for this user and service
    const { data: existingData } = await supabase
      .from('user_auth_tokens')
      .select('id')
      .eq('user_id', user.id)
      .eq('provider_type', providerType)
      .single();
    
    if (existingData) {
      // Update existing record
      const { error } = await supabase
        .from('user_auth_tokens')
        .update({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          expires_at: processedExpiresAt,
          user_identifier: tokenData.user_identifier,
          scopes: tokenData.scopes,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingData.id);
      
        
      if (error) {
        console.error('Error updating auth token in Supabase:', error);
        return false;
      }
      
      console.log(`Updated ${providerType} token in Supabase for user ${user.id}`);
      return true;
    } else {
      // Insert new record
      const { error } = await supabase
        .from('user_auth_tokens')
        .insert({
          user_id: user.id,
          provider_type: providerType,
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          expires_at: processedExpiresAt,
          user_identifier: tokenData.user_identifier,
          scopes: tokenData.scopes
        });
      
      if (error) {
        console.error('Error saving auth token to Supabase:', error);
        return false;
      }
      
      console.log(`Saved ${providerType} token to Supabase for user ${user.id}`);
      return true;
    }
  } catch (error) {
    console.error('Error in saveAuthToken:', error);
    return false;
  }
};

/**
 * Retrieves a music service authentication token from Supabase for the current user
 */
export const getAuthToken = async (providerType: MusicServiceType): Promise<AuthToken | null> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('Cannot retrieve token: No authenticated user');
      return null;
    }
    
    // Check if this token has been refreshed in the last hour to prevent excessive calls
    const lastRefreshKey = `last_refresh_${providerType}`;
    const lastRefreshTime = localStorage.getItem(lastRefreshKey);
    
    if (lastRefreshTime) {
      const hourAgo = Date.now() - (60 * 60 * 1000); // 1 hour ago in milliseconds
      const lastRefreshTimestamp = parseInt(lastRefreshTime);
      
      if (lastRefreshTimestamp > hourAgo) {
        console.log(`Skipping ${providerType} token refresh - last refresh was within the hour`);
        // Continue with the cached token
      } else {
        console.log(`More than an hour since last ${providerType} token refresh`);
        // Update the refresh timestamp
        localStorage.setItem(lastRefreshKey, Date.now().toString());
      }
    } else {
      // First time retrieval, set the timestamp
      localStorage.setItem(lastRefreshKey, Date.now().toString());
    }
    
    // Get the token from Supabase
    const { data, error } = await supabase
      .from('user_auth_tokens')
      .select('access_token, refresh_token, expires_at, user_identifier, scopes, updated_at')
      .eq('user_id', user.id)
      .eq('provider_type', providerType)
      .single();
    
    if (error) {
      console.error(`Error retrieving ${providerType} token from Supabase:`, error);
      return null;
    }
    
    if (!data) {
      console.log(`No ${providerType} token found for user ${user.id}`);
      return null;
    }
    
    console.log(`Retrieved ${providerType} token from Supabase for user ${user.id}`);
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      user_identifier: data.user_identifier,
      scopes: data.scopes
    };
  } catch (error) {
    console.error('Error in getAuthToken:', error);
    return null;
  }
};

/**
 * Deletes a music service authentication token from Supabase for the current user
 */
export const deleteAuthToken = async (providerType: MusicServiceType): Promise<boolean> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('Cannot delete token: No authenticated user');
      return false;
    }
    
    // Delete the token from Supabase
    const { error } = await supabase
      .from('user_auth_tokens')
      .delete()
      .eq('user_id', user.id)
      .eq('provider_type', providerType);
    
    if (error) {
      console.error(`Error deleting ${providerType} token from Supabase:`, error);
      return false;
    }
    
    console.log(`Deleted ${providerType} token from Supabase for user ${user.id}`);
    return true;
  } catch (error) {
    console.error('Error in deleteAuthToken:', error);
    return false;
  }
}; 